import {debounce} from "../../../../lib";

class Listbox {
    constructor($root) {
        this.$root = $root;
        this.$button = this.$root.querySelector(`.button[data-for="${this.$root.id}"]`);
        this.$listboxContainer = this.$root.querySelector(`.listbox_container[data-for="${this.$root.id}"]`);
        this.$listboxOpts = this.$listboxContainer.querySelector(`.listbox_opt-list`);
        this.$optionList = this.$listboxOpts.querySelectorAll('[role="option"]');
        this.$listboxSearchInput = this.$listboxContainer.querySelector('.listbox_search-input');
        this.$listboxSearchCancel = this.$listboxContainer.querySelector('.listbox_search-cancel');
        this.$listboxOptNotFound = this.$listboxContainer.querySelector('.listbox_not-found');
        this.$focusedOption = null;

        this.keyCode = {
            PAGE_UP: 'PageUp',
            PAGE_DOWN: 'PageDown',
            UP: 'ArrowUp',
            DOWN: 'ArrowDown',
            HOME: 'Home',
            END: 'End',
            SPACE: ' ',
            BACKSPACE: 'Backspace',
            DELETE: 'Delete',
            ENTER: 'Enter',
            ESC: 'Escape'
        }

        this.init();
    }

    init() {
        this.registerEvents();
        this.checkScrollbar();
    }

    checkScrollbar() {
        if (this.$listboxOpts.scrollHeight > this.$listboxOpts.offsetHeight) {
            this.$listboxOpts.classList.add('-scrollbar');
        } else {
            this.$listboxOpts.classList.remove('-scrollbar');
        }
    }

    /**
     * @desc
     *  Register events for the listbox interactions
     */
    registerEvents() {
        this.$root.addEventListener('keydown', this.checkKeyPress.bind(this));
        this.$root.addEventListener('click', this.checkClickItem.bind(this));

        if (this.$listboxSearchInput) {
            this.$listboxSearchInput.addEventListener('input', debounce(this.handleSearch.bind(this), 5));
            this.$listboxSearchCancel.addEventListener('click', this.handleSearchCancel.bind(this));
        }
    }

    handleSearch(e) {
        this.$optionList = this.$listboxOpts.querySelectorAll('[role="option"]');
        const searchValue = e.target.value.toLowerCase();
        let searchResultCount = 0;

        if (searchValue.length > 0) {
            this.$listboxContainer.classList.add('-search');
        } else {
            this.$listboxContainer.classList.remove('-search');
        }

        this.$optionList.forEach(($option) => {
            const searchValueIndex = $option.textContent
                .toLowerCase()
                .indexOf(searchValue);

            if (searchValueIndex === -1) {
                $option.classList.add('-hidden');
            } else {
                $option.classList.remove('-hidden');
                searchResultCount++;
            }
        });

        if (searchResultCount === 0) {
            this.$listboxOptNotFound.classList.remove('-hidden');
        } else {
            this.$listboxOptNotFound.classList.add('-hidden');
        }

        const $optWithClass = this.$listboxOpts.querySelector('.-last');
        if ($optWithClass) {
            $optWithClass.classList.remove('-last');
        }
        this.addClassToLastVisibleOption();

        this.checkScrollbar();
    }

    handleSearchCancel() {
        this.$optionList.forEach(($option) => {
            $option.classList.remove('-hidden');
        });

        this.$listboxSearchInput.value = '';
        this.$listboxContainer.classList.remove('-search');

        const $optWithClass = this.$listboxOpts.querySelector('.-last');
        if ($optWithClass) {
            $optWithClass.classList.remove('-last');
        }

        this.$listboxOptNotFound.classList.add('-hidden');

        this.checkScrollbar();
    }

    addClassToLastVisibleOption(className = '-last') {
        const $visibleOpts = this.$listboxOpts.querySelectorAll('[role="option"]:not(.-hidden)');

        if ($visibleOpts.length) {
            $visibleOpts[$visibleOpts.length - 1].classList.add(className);
        }
    }

    /**
     * @desc
     *  Focus on the first option
     */
    focusFirstOption() {
        const $firstItem = this.$root.querySelector('[role="option"]');

        if ($firstItem) {
            this.focusOption($firstItem);
        }
    }

    /**
     * @desc
     *  Focus on the last option
     */
    focusLastOption() {
        const $itemList = this.$root.querySelectorAll('[role="option"]');

        if ($itemList.length) {
            this.focusOption($itemList[$itemList.length - 1]);
        }
    }

    /**
     * @desc
     *  Handle various keyboard controls; UP/DOWN will shift focus; SPACE selects
     *  an item.
     *
     * @param e
     *  The keydown event object
     */
    checkKeyPress(e) {
        const key = e.key;
        let $nextItem = this.$focusedOption;

        switch (key) {
            case this.keyCode.UP:
            case this.keyCode.DOWN:
                e.preventDefault();

                if (!$nextItem) {
                    this.focusFirstOption();
                    return;
                }

                if (key === this.keyCode.UP) {
                    $nextItem = this.getPrevOpt($nextItem);
                }
                else {
                    $nextItem = this.getNextOpt($nextItem);
                }

                if ($nextItem) {
                    this.focusOption($nextItem);
                }

                break;
            case this.keyCode.HOME:
                e.preventDefault();
                this.focusFirstOption();
                break;
            case this.keyCode.END:
                e.preventDefault();
                this.focusLastOption();
                break;
            case this.keyCode.ENTER:
                e.preventDefault();
                this.selectOption(this.$root.querySelector('[data-focused="true"]'));
        }
    }

    getNextOpt($curOpt) {
        const $next = $curOpt.nextElementSibling;

        if (!$next) {
            return this.$focusedOption;
        }

        if (!$next.classList.contains('-hidden') && $next.classList.contains('listbox_option')) {
            return $next;
        } else {
            return this.getNextOpt($next);
        }
    }

    getPrevOpt($curOpt) {
        const $prev = $curOpt.previousElementSibling;

        if (!$prev) {
            return this.$focusedOption;
        }

        if (!$prev.classList.contains('-hidden') && $prev.classList.contains('listbox_option')) {
            return $prev;
        } else {
            return this.getPrevOpt($prev);
        }
    }

    /**
     * @desc
     *  Check if an item is clicked on. If so, focus on it and select it.
     *
     * @param e
     *  The click event object
     */
    checkClickItem(e) {
        const $option = e.target.closest('[role="option"]');

        if ($option) {
            this.selectOption($option);
        }
    }

    /**
     * @desc
     *  Defocus the specified item
     *
     * @param $element
     *  The element to defocus
     */
    unselectOption($element) {
        if (!$element) {
            return;
        }
        $element.removeAttribute('aria-selected');
        $element.classList.remove('-focused');
        $element.removeAttribute('data-focused');
    }

    unselectActive() {
        this.unselectOption(this.$root.querySelector('[aria-selected="true"]'));
    }

    focus() {
        this.$listboxOpts.focus();
        const $selectedOption = this.$root.querySelector('[aria-selected="true"]');

        if ($selectedOption) {
            this.focusOption($selectedOption);
        }
    }

    focusOption($element) {
        this.unfocusPrevOption();
        $element.classList.add('-focused');
        $element.setAttribute('data-focused', 'true');
        this.$focusedOption = $element;
    }

    unfocusPrevOption() {
        const $focused = this.$root.querySelector('[data-focused="true"]');
        if (!$focused) return;

        $focused.classList.remove('-focused');
        $focused.removeAttribute('data-focused');
    }

    /**
     * @desc
     *  Focus on the specified item
     *
     * @param element
     *  The element to focus
     *
     * @emit bool
     * Should emit event
     */
    selectOption($element, emit = true) {
        this.unselectActive();

        $element.setAttribute('aria-selected', 'true');
        this.focusOption($element);

        this.$root.dataset.selected = $element.dataset.value;

        if (this.$root.scrollHeight > this.$root.clientHeight) {
            const scrollBottom = this.$root.clientHeight + this.$root.scrollTop;
            const elementBottom = $element.offsetTop + $element.offsetHeight;
            if (elementBottom > scrollBottom) {
                this.$root.scrollTop = elementBottom - this.$root.clientHeight;
            }
            else if ($element.offsetTop < this.$root.scrollTop) {
                this.$root.scrollTop = $element.offsetTop;
            }
        }

        if (emit) {
            this.emitSelectEvent($element);
        }
    }

    emitSelectEvent($element) {
        const event = new CustomEvent('select', { detail: { $selectedOption: $element } });
        this.$root.dispatchEvent(event);
    }

}


/**
 * Collapsible Dropdown Listbox
 * @function onload
 * @desc Initialize the listbox once the page has loaded
 */
class ListboxButton {
    constructor(listbox) {
        this.$root = listbox.$button;
        this.$buttonContent = this.$root.querySelector('.button-content');
        this.listbox = listbox;
        this.initialContent = this.$buttonContent.innerHTML;
        this.registerEvents();
    }

    registerEvents() {
        this.$root.addEventListener('click', this.toggleExpandListbox.bind(this));
        this.$root.addEventListener('keyup', this.checkShow.bind(this));
        this.listbox.$root.addEventListener('select', this.onSelectChange.bind(this));
        this.listbox.$listboxOpts.addEventListener('keyup', this.checkHide.bind(this));
        this.listbox.$listboxOpts.addEventListener('click', this.hideListbox.bind(this));
        window.addEventListener('click', (e) => {
            if (e.target.closest(`.listbox`) != this.$root.closest('.listbox')) {
                this.hideListbox();
            }
        });
    }

    checkShow(e) {
        var key = e.key;

        switch (key) {
            case this.listbox.keyCode.UP:
            case this.listbox.keyCode.DOWN:
                e.preventDefault();
                this.showListbox();
                // this.listbox.checkKeyPress(e);
                break;
        }
    }

    checkHide(e) {
        var key = e.key;

        switch (key) {
            case this.listbox.keyCode.ENTER:
            case this.listbox.keyCode.ESC:
                e.preventDefault();
                this.hideListbox();
                this.$root.focus();
                break;
        }
    }

    toggleExpandListbox(e) {
        e.preventDefault();

        if (this.listbox.$root.getAttribute('data-expanded')) {
            this.hideListbox();
        } else {
            this.showListbox();
        }
    }

    showListbox() {
        this.listbox.$listboxContainer.classList.remove('-hidden');
        this.listbox.checkScrollbar();
        this.listbox.$root.setAttribute('data-expanded', 'true');
    }

    hideListbox() {
        this.listbox.$listboxContainer.classList.add('-hidden');
        this.listbox.$root.removeAttribute('data-expanded');
        this.listbox.focus();

        if (this.listbox.$listboxSearchInput) {
            this.listbox.handleSearchCancel();
        }
    }

    onSelectChange(e) {
        if (e.detail) {
            this.$buttonContent.innerHTML = e.detail.$selectedOption.innerHTML.trim();
        }
    }

    setInitialContent() {
        this.$buttonContent.innerHTML = this.initialContent.trim();
    }
}


export default function init($root) {
    const listbox = new Listbox($root);
    const listboxButton = new ListboxButton(listbox);

    return {
        listbox,
        listboxButton
    }
}